import React from 'react';
import { Heart, RefreshCw, XCircle, TrendingUp, TrendingDown, Minus, AlertTriangle, Ban } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';

const WIDGET_AGENTS = ['Transcript Analysis Agent (Jamba)'];
const WIDGET_PROMPT = `Analyze the sentiment and emotional tone of this fraud claim call transcript.

Analyze: overall sentiment, customer emotional state/stress/urgency,
agent tone/empathy, sentiment trend, key emotional moments, vulnerability signals.

OUTPUT FORMAT (JSON):
{
  "overall_sentiment": "negative",
  "overall_score": -0.6,
  "customer": { "sentiment", "stress_level", "urgency", "emotions": [], "score" },
  "agent": { "sentiment", "empathy_level", "tone", "score" },
  "trend": "stable | improving | declining",
  "trend_description": "...",
  "key_moments": [{ "timestamp", "emotion", "quote", "severity" }],
  "vulnerability_signals": [...],
  "escalation_risk": "low | medium | high"
}`;

const SENTIMENT_COLORS = {
  positive: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500', dot: 'bg-green-400' },
  neutral:  { bg: 'bg-gray-50',  border: 'border-gray-200',  text: 'text-gray-600',  bar: 'bg-gray-400',  dot: 'bg-gray-400'  },
  negative: { bg: 'bg-red-50',   border: 'border-red-200',   text: 'text-red-700',   bar: 'bg-red-500',   dot: 'bg-red-400'   },
  distressed: { bg: 'bg-red-50', border: 'border-red-200',   text: 'text-red-700',   bar: 'bg-red-500',   dot: 'bg-red-400'   },
};

const RISK_STYLES = {
  high:   'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:    'bg-green-100 text-green-700 border-green-200',
};

const LEVEL_STYLES = {
  high:     'bg-red-100 text-red-700',
  moderate: 'bg-yellow-100 text-yellow-700',
  medium:   'bg-yellow-100 text-yellow-700',
  low:      'bg-green-100 text-green-700',
};

const scoreToPercent = (score) => Math.round(((score ?? 0) + 1) / 2 * 100);

const ScoreBar = ({ score, colorClass }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 bg-gray-100 rounded-full h-1.5">
      <div className={`${colorClass} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${scoreToPercent(score)}%` }} />
    </div>
    <span className="text-xs text-gray-500 w-8 text-right">{score >= 0 ? '+' : ''}{(score ?? 0).toFixed(1)}</span>
  </div>
);

const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <TrendingUp size={13} className="text-green-500" />;
  if (trend === 'declining') return <TrendingDown size={13} className="text-red-500" />;
  return <Minus size={13} className="text-gray-400" />;
};

const SentimentAnalysis = ({ result, isLoading, error, onRefresh }) => {
  const notApplicable = result?.not_applicable === true;
  const data = result?.data;
  const hasData = !!data;

  const overallColors = SENTIMENT_COLORS[data?.overall_sentiment] || SENTIMENT_COLORS.neutral;
  const customerColors = SENTIMENT_COLORS[data?.customer?.sentiment] || SENTIMENT_COLORS.neutral;

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <Heart size={15} style={{ color: '#000080' }} />
          <h3 className="font-semibold text-gray-800 text-sm">Sentiment Analysis</h3>
          {isLoading && hasData && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#000080' }} title="Updating..." />}
        </div>
        <div className="flex items-center gap-1">
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          <button onClick={onRefresh} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40" title="Re-analyze">
            <RefreshCw size={14} className={isLoading && !hasData ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="p-4">
        {notApplicable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            <Ban size={22} className="text-gray-300" />
            <p className="text-xs mt-1 text-gray-500">No sentiment data — not a fraud claim call</p>
          </div>
        )}

        {!hasData && !notApplicable && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#000080', borderTopColor: 'transparent' }} />
              : <Heart size={22} className="opacity-30" />}
            <p className="text-xs mt-1">{isLoading ? 'Analyzing sentiment...' : 'Waiting for transcript...'}</p>
          </div>
        )}

        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {hasData && (
          <>

            {/* Overall + Escalation Risk */}
            <div className="flex items-start gap-2 mb-3">
              <div className={`flex-1 rounded-lg px-3 py-2 border ${overallColors.bg} ${overallColors.border}`}>
                <p className="text-xs text-gray-500 mb-0.5 uppercase tracking-wide font-medium">Overall</p>
                <p className={`text-sm font-bold capitalize ${overallColors.text}`}>{data.overall_sentiment}</p>
                <ScoreBar score={data.overall_score} colorClass={overallColors.bar} />
              </div>
              {data.escalation_risk && (
                <div className={`px-3 py-2 rounded-lg border text-xs font-semibold flex flex-col items-center gap-0.5 ${RISK_STYLES[data.escalation_risk] || RISK_STYLES.medium}`}>
                  <AlertTriangle size={12} />
                  <span className="capitalize">{data.escalation_risk}</span>
                  <span className="font-normal opacity-70">risk</span>
                </div>
              )}
            </div>

            {/* Customer */}
            {data.customer && (
              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100 mb-3">
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Customer</p>
                <p className={`text-xs font-medium capitalize mb-1 ${customerColors.text}`}>{data.customer.sentiment}</p>
                <ScoreBar score={data.customer.score} colorClass={customerColors.bar} />
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {data.customer.stress_level && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${LEVEL_STYLES[data.customer.stress_level] || 'bg-gray-100 text-gray-600'}`}>
                      Stress: {data.customer.stress_level}
                    </span>
                  )}
                  {data.customer.urgency && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${LEVEL_STYLES[data.customer.urgency] || 'bg-gray-100 text-gray-600'}`}>
                      Urgency: {data.customer.urgency}
                    </span>
                  )}
                </div>
                {data.customer.emotions?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {data.customer.emotions.map((e, i) => (
                      <span key={i} className="text-xs bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full capitalize">{e}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Trend */}
            {data.trend && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg mb-3">
                <TrendIcon trend={data.trend} />
                <span className="text-xs font-medium text-gray-700 capitalize">{data.trend}</span>
                {data.trend_description && (
                  <span className="text-xs text-gray-500 truncate">— {data.trend_description}</span>
                )}
              </div>
            )}

            {/* Vulnerability Signals */}
            {data.vulnerability_signals?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Vulnerability Signals</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.vulnerability_signals.map((s, i) => (
                    <span key={i} className="text-xs bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-full capitalize">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Key Moments */}
            {/* {data.key_moments?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1.5">Key Moments</p>
                <div className="space-y-1.5">
                  {data.key_moments.map((m, i) => (
                    <div key={i} className={`rounded-lg p-2 border text-xs ${m.severity === 'high' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`font-semibold capitalize ${m.severity === 'high' ? 'text-red-600' : 'text-gray-600'}`}>{m.emotion}</span>
                        {m.timestamp && <span className="text-gray-400">{m.timestamp}</span>}
                      </div>
                      {m.quote && <p className="text-gray-600 italic">"{m.quote}"</p>}
                    </div>
                  ))}
                </div>
              </div>
            )} */}
          </>
        )}
      </div>
    </div>
  );
};

export default SentimentAnalysis;
